import { z } from 'zod'

/**
 * Permission catalog, the current user's permissions and effective permissions.
 * The catalog is declared by backend modules; never hard-code permission groups in the UI.
 */

export const permissionScopeSchema = z.enum(['GLOBAL', 'NODE', 'SUBTREE', 'GROUP_NODE', 'GROUP_SUBTREE'])
export type PermissionScope = z.infer<typeof permissionScopeSchema>

export const actionTargetSchema = z.enum(['WORKSPACE', 'RESOURCE', 'RECORD_GROUP_CONTAINER'])
export const resourceHierarchySchema = z.enum(['STANDALONE', 'RECORD_GROUP_TREE', 'RECORD_GROUP_MEMBER'])

export const catalogActionSchema = z.object({
  key: z.string(),
  label: z.string(),
  description: z.string(),
  target: actionTargetSchema,
  billingSensitive: z.boolean(),
  scopes: z.array(permissionScopeSchema),
  /** Keys this permission includes (granted along with it, on the matching scope). */
  includes: z.array(z.string()).optional().default([]),
})

export const catalogResourceSchema = z.object({
  resourceType: z.string(),
  label: z.string(),
  category: z.string(),
  hierarchy: resourceHierarchySchema,
  actions: z.array(catalogActionSchema),
})

export const permissionCatalogSchema = z.object({
  categories: z.array(z.object({ id: z.string(), label: z.string(), order: z.number() })),
  resources: z.array(catalogResourceSchema),
})

export const myPermissionsSchema = z.object({
  userId: z.string().uuid(),
  workspaceId: z.string().uuid(),
  owner: z.boolean(),
  admin: z.boolean(),
  developer: z.boolean(),
  globalKeys: z.array(z.string()),
  anywhereKeys: z.array(z.string()),
  // True if some record group is reachable, possibly only by name to reach shared content below it.
  recordGroupAccess: z.boolean().optional().default(false),
})

export const permissionSourceSchema = z.object({
  // IMPLIED: included in another permission the user holds (never revocable on its own)
  type: z.enum(['ROLE', 'OVERRIDE', 'ADMIN', 'OWNER', 'IMPLIED']),
  grantId: z.string().uuid().nullable(),
  roleId: z.string().uuid().nullable(),
  roleName: z.string().nullable(),
  /** For IMPLIED: the held permission key that includes this one. */
  impliedBy: z.string().nullable().optional(),
})

export const effectivePermissionSchema = z.object({
  permissionKey: z.string(),
  resourceType: z.string(),
  resourceId: z.string().uuid().nullable(),
  scope: permissionScopeSchema,
  sources: z.array(permissionSourceSchema),
})

export const effectivePermissionsSchema = z.object({
  userId: z.string().uuid(),
  owner: z.boolean(),
  admin: z.boolean(),
  roles: z.array(z.object({ id: z.string().uuid(), name: z.string(), roleType: z.string() })),
  permissions: z.array(effectivePermissionSchema),
})

/** Body of a 409 PERMISSION_ALREADY_INHERITED response. */
export const permissionAlreadyInheritedSchema = z.object({
  code: z.literal('PERMISSION_ALREADY_INHERITED'),
  covered: z.array(
    z.object({
      permissionKey: z.string(),
      scope: permissionScopeSchema,
      resourceId: z.string().uuid().nullable(),
      coveredBy: z.array(permissionSourceSchema),
    })
  ),
})

export type CatalogAction = z.infer<typeof catalogActionSchema>
export type CatalogResource = z.infer<typeof catalogResourceSchema>
export type PermissionCatalog = z.infer<typeof permissionCatalogSchema>
export type MyPermissions = z.infer<typeof myPermissionsSchema>
export type PermissionSource = z.infer<typeof permissionSourceSchema>
export type EffectivePermission = z.infer<typeof effectivePermissionSchema>
export type EffectivePermissions = z.infer<typeof effectivePermissionsSchema>
export type PermissionAlreadyInherited = z.infer<typeof permissionAlreadyInheritedSchema>
